import { UserOutlined } from '@ant-design/icons';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history } from '@umijs/max';
import { Avatar, Space } from 'antd';
import type { LoginUser } from '@/services/auth';
import { AvatarDropdown, AvatarName, Footer, Question } from '@/components';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import { resolveAdminApiBaseUrl } from '@/utils/resolveAdminApiBaseUrl';
import { getAdminBrandTitle } from '@/utils/brandTitle';
import { brandAssets } from '@/utils/brandAssets';
import { brandPrimaryColor } from '@/utils/prodBTheme';
import { isProdBEnv } from '@/utils/isProdBEnv';
import '@ant-design/v5-patch-for-react-19';
import React from 'react';

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/user/login';

/**
 * 顶栏右侧（帮助 + 用户下拉）。单独抽出，避免被 settings 展开覆盖。
 */
const renderHeaderRight = () => (
  <Space size={16} style={{ flexShrink: 0 }}>
    <Question key="doc" />
    <AvatarDropdown>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          cursor: 'pointer',
          padding: '0 4px',
        }}
      >
        <Avatar size="small" style={{ backgroundColor: brandPrimaryColor() }} icon={<UserOutlined />} />
        <AvatarName />
      </span>
    </AvatarDropdown>
  </Space>
);

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: LoginUser;
  name?: string;
}> {
  const storedUser = localStorage.getItem('edu_user');
  let currentUser: LoginUser | undefined;
  if (storedUser) {
    try {
      currentUser = JSON.parse(storedUser) as LoginUser;
    } catch {
      currentUser = undefined;
    }
  }
  if (typeof document !== 'undefined' && isProdBEnv()) {
    document.documentElement.dataset.brand = 'prod-b';
  }
  return {
    currentUser,
    settings: {
      ...(defaultSettings as Partial<LayoutSettings>),
      title: getAdminBrandTitle(),
      logo: brandAssets.logo,
    },
    name: currentUser?.username || currentUser?.mobile,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({ initialState, setInitialState }) => {
  return {
    /** 必须先展开，再覆盖：否则 SettingDrawer/默认配置可能带上未知字段覆盖 rightContentRender */
    ...initialState?.settings,

    onPageChange: () => {
      const { location } = history;
      const storedUser = localStorage.getItem('edu_user');
      const isLoggedIn = !!(initialState?.currentUser || storedUser);
      if (!isLoggedIn && location.pathname !== loginPath) {
        history.push(loginPath);
      }
    },
    bgLayoutImgList: [...brandAssets.layoutBackgroundImages],
    links: [],
    menuHeaderRender: undefined,
    childrenRender: (children) => {
      return (
        <>
          {children}
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  /** 保持顶栏与 mix，避免改回 side 后用户区又落到侧栏底 */
                  settings: {
                    ...settings,
                    fixedHeader: true,
                    layout: 'mix',
                    splitMenus: false,
                  },
                }));
              }}
            />
          )}
        </>
      );
    },

    footerRender: () => <Footer />,
    /**
     * 不要同时配置 avatarProps：pro-layout ActionsContent 会优先走 rightActionsRender（只渲染 title 纯文本），
     * 从而整段忽略 rightContentRender，导致右上角「admin」无法点开下拉、没有退出。
     * 用户区只在 rightContentRender 里用 AvatarDropdown 即可。
     */
    /** 必须放在最后，避免被 ...settings 覆盖；Umi 插件会对本函数再包一层 */
    rightContentRender: () => renderHeaderRight(),
    /** 正式 B 按域名/runtime 显示「医美管理」，避免构建环境不一致时仍显示「教育管理」 */
    title: getAdminBrandTitle(),
    /**
     * 必须最后：ProLayout 在 fixedHeader=false 时不渲染顶栏，用户信息会落到侧栏底部。
     * 放在末尾确保覆盖 config 与 initialState.settings 里的 fixedHeader。
     */
    fixedHeader: true,
  };
};

/**
 * @name request 配置
 */
export const request: RequestConfig = {
  baseURL: resolveAdminApiBaseUrl(),
  ...errorConfig,
};
